import type { ReactNode } from 'react';

import { Footer } from '@/Components/Footer';
import { Header, type HeaderNavItem } from '@/Components/Header';
import { JsonLdScript } from '@/Components/JsonLdScript';
import { MetaHead } from '@/Components/MetaHead';
import { MobileCTABar } from '@/Components/MobileCTABar';
import { MobileHeader } from '@/Components/MobileHeader';
import { UtilityBar } from '@/Components/UtilityBar';
import { useLocale, useT } from '@/Lib/i18n';

const PHONE = '1-833-INJURY-1';

type Props = {
    children: ReactNode;
    active?:
        | 'practice-areas'
        | 'states'
        | 'get-matched'
        | 'for-attorneys'
        | 'resources';
};

/**
 * Standard public chrome — utility bar + desktop header + mobile header,
 * footer, and the floating mobile CTA. Drops `MetaHead` and `JsonLdScript`
 * once so every page that uses this layout gets canonical/OG/JSON-LD for
 * free.
 *
 * Nav labels + the locale switcher pull from Inertia shared props so
 * switching to ?lang=es translates the chrome along with the page body.
 */
export function PublicLayout({ children, active }: Props) {
    const t = useT('nav');
    const { locale, switchTo } = useLocale();

    const nav: HeaderNavItem[] = [
        { key: 'practice-areas', label: t('practice_areas'), href: '/' },
        { key: 'states', label: t('states'), href: '/' },
        { key: 'resources', label: t('resources'), href: '/resources' },
        {
            key: 'for-attorneys',
            label: t('for_attorneys'),
            href: '/for-attorneys',
        },
    ];

    const localeLabel = locale === 'es' ? 'ES' : 'EN';
    const handleLocaleClick = () => switchTo(locale === 'es' ? 'en' : 'es');

    return (
        <>
            <MetaHead />
            <JsonLdScript />

            <UtilityBar
                phone={PHONE}
                locale={localeLabel}
                onLocaleClick={handleLocaleClick}
            />
            {active !== undefined ? (
                <Header phone={PHONE} active={active} nav={nav} />
            ) : (
                <Header phone={PHONE} nav={nav} />
            )}
            <MobileHeader phone={PHONE} />

            <main id="main" className="min-h-[60vh]">
                {children}
            </main>

            <Footer />
            <MobileCTABar phone={PHONE} />
        </>
    );
}
